<app-step-dot-menu
  [projectId]="projectId"
  testId="step3-2-dot-menu-button"
  [preselectedRole]="ProjectRoleType.CONTRIBUTOR"
  [preselectedAccessiblePages]="[ProjectStepType.RFD_DEMONSTRATION_USE_CASES]"
></app-step-dot-menu>

<div class="mt-5" data-testid="use-cases-page" *transloco="let t">
  @if (loading()) {
    <app-loading />
  } @else if (prerequisiteInfo()) {
    <p-card>
      <app-no-data
        [icon]="prerequisiteInfo()!.icon"
        [label]="prerequisiteInfo()!.titleKey"
        [description]="prerequisiteInfo()!.messageKey"
      />
    </p-card>
  } @else if (shouldShowContent()) {
    <p-tabs
      appTabsUnsavedReveal
      [value]="activeTabIndex()"
      (valueChange)="onTabChange($event!)"
      data-testid="use-cases-tabs"
      scrollable
    >
      <p-tablist>
        @for (tab of tabs(); track tab.agendaId; let i = $index) {
          <p-tab
            [value]="i"
            [appTabStatus]="getTabStatus(tab)"
            [appTabStatusUnsaved]="tabHasChanges(tab)"
            [attr.data-testid]="'usecase-tab-' + tab.agendaId"
          >
            {{ tab.agendaTranslations | translateDirectus: 'topic' }}
          </p-tab>
        }
      </p-tablist>

      <p-tabpanels class="mt-6">
        @for (tab of tabs(); track tab.agendaId; let i = $index) {
          <p-tabpanel [value]="i" [attr.data-testid]="'usecase-tabpanel-' + tab.agendaId">
            <div class="card" data-testid="use-cases-card">
              @if (currentTab() && i === activeTabIndex()) {
                @if (tabContentLoading()) {
                  <app-loading />
                } @else {
                  <div class="space-y-3">
                    <!-- Summary Section -->
                    <div class="sticky top-[52px] z-20 bg-(--surface-card) pt-3 -mt-3 pb-3">
                      <app-demonstration-summary
                        [totalUseCases]="currentTabTotalUseCases()"
                        [includedUseCases]="currentTabIncludedUseCases()"
                        [plannedDuration]="currentTabPlannedDuration()"
                        [actualDuration]="currentTabActualDuration()"
                        [durationDifference]="currentTabDurationDifference()"
                      />
                    </div>

                    <!-- Description Section -->
                    @if (tab.agendaTranslations) {
                      <div class="pb-4">
                        <div class="p-4 bg-third-level rounded-lg">
                          <p class="text-gray-300 m-0">
                            {{ tab.agendaTranslations | translateDirectus: 'description' }}
                          </p>
                        </div>
                      </div>
                    }

                    <div class="flex items-end justify-end gap-3 pb-4">
                      <div class="flex flex-col gap-1">
                        <span class="text-sm text-gray-400">{{ t('common.search') }}</span>
                        <app-global-search
                          testId="use-cases-search"
                          (searchValue)="searchTerm.set($event)"
                        />
                      </div>
                      <div class="flex flex-col gap-1">
                        <span class="text-sm text-gray-400">
                          {{ t('project.steps.rfd.useCase.filter.strengthAttributes') }}
                        </span>
                        <app-dropdown-filter
                          [options]="strengthAttributeOptions()"
                          [value]="maxStrengthAttributes()"
                          (valueChange)="maxStrengthAttributes.set($event!)"
                        />
                      </div>
                      <div class="flex flex-col gap-1">
                        <span class="text-sm text-gray-400">
                          {{ t('project.steps.rfd.useCase.filter.minFunctions') }}
                        </span>
                        <app-dropdown-filter
                          [options]="functionCountOptions()"
                          [value]="maxFunctions()"
                          (valueChange)="maxFunctions.set($event!)"
                        />
                      </div>
                    </div>

                    <!-- Use Cases List grouped by Module Function -->
                    <p-accordion
                      [multiple]="true"
                      [value]="openAccordionPanels()"
                      (valueChange)="onAccordionValueChange($event)"
                    >
                      @for (group of currentTabGroups(); track group.id) {
                        <p-accordion-panel [value]="group.id">
                          <p-accordion-header>
                            @if (group.id !== -1) {
                              <span class="text-xl font-bold text-white">
                                {{ group.translations | translateDirectus: 'name' }}
                              </span>
                            } @else {
                              <span class="text-xl font-bold text-white">
                                {{ t('common.other') }}
                              </span>
                            }
                          </p-accordion-header>
                          <p-accordion-content>
                            <div class="use-cases-wrapper overflow-x-auto">
                              <app-badge-list
                                [items]="group.badgeItems"
                                [headerInputTemplate]="headerInputTemplate"
                                [contentTemplate]="contentTemplate"
                                [attr.data-testid]="
                                  'use-cases-badge-list-' + tab.agendaId + '-' + group.id
                                "
                              >
                              </app-badge-list>
                            </div>
                          </p-accordion-content>
                        </p-accordion-panel>
                      }
                    </p-accordion>

                    <ng-template #headerInputTemplate let-item>
                      <app-demonstration-use-case-item
                        [useCase]="item.originalItem"
                        [included]="includedOf(item.originalItem)"
                        [disabled]="isCurrentStepLocked()"
                        [lockedTooltip]="stepLockedTooltip()"
                        (includedChange)="onUseCaseIncludedChange(item.originalItem, $event)"
                      />
                    </ng-template>

                    <ng-template #contentTemplate let-item>
                      <p
                        class="m-0 text-gray-300"
                        [attr.data-testid]="'usecase-description-' + item.key"
                      >
                        {{ item.translations | translateDirectus: 'description' }}
                      </p>
                    </ng-template>

                    <!-- Legend Section -->
                    <app-demonstration-score-legend />

                    <!-- Save Button -->
                    @if (currentTabHasChanges() && !isCurrentStepLocked()) {
                      <div #saveActions class="flex justify-end mt-3" data-testid="save-actions">
                        <app-project-step-save-button
                          [loading]="saving()"
                          [disabled]="saving() || !canSave() || isCurrentStepLocked()"
                          (clicked)="onSave()"
                          [pTooltip]="
                            hasDurationWarning()
                              ? t('project.steps.rfd.useCase.actualDuration.warning')
                              : ''
                          "
                        ></app-project-step-save-button>
                      </div>
                    }
                  </div>
                }
              }
            </div>
          </p-tabpanel>
        }
      </p-tabpanels>
    </p-tabs>
  } @else {
    <p-card>
      <app-no-data
        icon="pi pi-inbox"
        label="project.steps.rfd.useCase.noUseCasesTitle"
        description="project.steps.rfd.useCase.noUseCasesMessage"
      />
    </p-card>
  }
</div>
